import { Icon } from '@ui/assets/icons/IconWrapper';
import { Button, Checkbox, DatePicker, Form, InputNumber, Space } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import {
    useFilterGroupsActions,
    useFilterMetadata,
    useFilterOptions,
    useMultiSelectActions,
    useMultiSelectValues,
} from '../hooks';
import { FilterInputType } from '../types/filter-metadata.types';
import type { FilterCondition, GroupOperator } from '../types/filter-panel.types';
import { ConditionValueInput } from './ConditionValueInput';
import { FieldSelector } from './FieldSelector';
import Styles from './Styles.module.scss';

interface FilterConditionRowProps {
    groupId: string;
    condition: FilterCondition;
    conditionIndex: number;
    groupOperator: GroupOperator;
}

export function FilterConditionRow({ groupId, condition, conditionIndex }: FilterConditionRowProps) {
    // Use constate hooks
    const { metadata } = useFilterMetadata();
    const { groupedFieldOptions } = useFilterOptions();
    const { multiSelectOperator } = useMultiSelectValues();
    const { setMultiSelectOperator } = useMultiSelectActions();
    const { updateCondition, removeCondition } = useFilterGroupsActions();

    const meta = metadata?.filters?.find(f => f.field === condition.field);
    const isMultiSelect = meta?.inputType === FilterInputType.MULTI_SELECT;
    const conditionKey = `${groupId}-${condition.id}`;
    const currentOp = multiSelectOperator[conditionKey] || 'OR';

    // Get the operator for this condition (use conditionOperator, default to 'AND' if not set)
    const conditionOp = condition.conditionOperator || 'AND';

    // Check if this is a measurement_type condition and get selected values
    const isMeasurementType = condition.field === 'measurement_type';
    const measurementTypeValues =
        isMeasurementType && condition.value
            ? Array.isArray(condition.value)
                ? condition.value
                : [condition.value]
            : [];

    // Get measurement value ranges from condition metadata (stored as (condition as any).measurementValueRanges)
    const measurementValueRanges = (condition as any).measurementValueRanges || {};

    // Check if this is a note_type or procedure_type condition
    const isNoteType = condition.field === 'note_type';
    const isProcedureType = condition.field === 'procedure_type';
    const hasNoteOrProcedureValues =
        (isNoteType || isProcedureType) &&
        condition.value &&
        (Array.isArray(condition.value) ? condition.value.length > 0 : true);

    // Get note type values (for per-type filtering)
    const noteTypeValues =
        isNoteType && condition.value ? (Array.isArray(condition.value) ? condition.value : [condition.value]) : [];

    // Get procedure type values (for per-type filtering)
    const procedureTypeValues =
        isProcedureType && condition.value
            ? Array.isArray(condition.value)
                ? condition.value
                : [condition.value]
            : [];

    // Get note/procedure filter metadata
    // For note_type and procedure_type, we now store filters per type: { "Radiology": { dateRange, countMin, countMax }, ... }
    const noteTypeFilters = (condition as any).noteTypeFilters || {};
    const procedureTypeFilters = (condition as any).procedureTypeFilters || {};

    return (
        <div
            key={condition.id}
            data-condition-id={condition.id}
            className="mb-[8px] flex flex-col items-center gap-[8px]"
        >
            {conditionIndex > 0 && (
                <Button
                    color={conditionOp === 'AND' ? 'blue' : 'green'}
                    className={Styles.logicalOperatorButton}
                    onClick={() => {
                        // Toggle between AND and OR
                        const newOp = conditionOp === 'AND' ? 'OR' : 'AND';
                        updateCondition(groupId, condition.id, {
                            conditionOperator: newOp,
                        });
                    }}
                >
                    {conditionOp}
                </Button>
            )}
            <Space.Compact className="w-full">
                <div className="flex w-full flex-col gap-2">
                    <div className="flex items-center gap-[8px]">
                        <div className="flex w-full flex-col gap-[4px]">
                            <div className="flex items-center justify-center gap-[6px]">
                                <Button
                                    danger
                                    className="!h-[20px] !w-[20px] !rounded-full hover:!bg-transparent focus:!bg-transparent [&_.ant-btn-icon]:h-[20px]"
                                    type="text"
                                    icon={
                                        <Icon
                                            name="CircleMinus"
                                            size={20}
                                        />
                                    }
                                    onClick={() => removeCondition(groupId, condition.id)}
                                />

                                <Form.Item className="mb-0! w-full">
                                    <FieldSelector
                                        value={condition.field}
                                        onChange={field =>
                                            updateCondition(groupId, condition.id, {
                                                field: field,
                                                operator: undefined,
                                                value: undefined,
                                            })
                                        }
                                        options={groupedFieldOptions}
                                        isMultiSelect={isMultiSelect}
                                        multiSelectOperator={currentOp}
                                        onMultiSelectOperatorChange={operator => {
                                            setMultiSelectOperator(prev => ({
                                                ...prev,
                                                [conditionKey]: operator,
                                            }));
                                        }}
                                    />
                                </Form.Item>
                            </div>

                            <div className="flex items-center gap-2">
                                <ConditionValueInput
                                    groupId={groupId}
                                    condition={condition}
                                />
                            </div>

                            {/* Show min/max value inputs for measurement_type when values are selected */}
                            {isMeasurementType && measurementTypeValues.length > 0 && (
                                <div className="mt-1 flex flex-col gap-2 border-t border-dashed border-[#e5e7eb] pt-2">
                                    {measurementTypeValues.map((type: string, idx: number) => {
                                        // Get range from condition metadata only
                                        const rangeFromMetadata = measurementValueRanges[type];
                                        const rangeValue = rangeFromMetadata
                                            ? [rangeFromMetadata.min, rangeFromMetadata.max]
                                            : [undefined, undefined];
                                        const isOutstanding = rangeFromMetadata?.outstanding || false;
                                        const isNormalOnly = rangeFromMetadata?.normalOnly || false;

                                        return (
                                            <div
                                                key={`${type}-${idx}`}
                                                className="flex flex-col gap-1"
                                            >
                                                <div className="flex items-center gap-1">
                                                    <span
                                                        className="w-[20%] truncate text-[12px] text-[#000]"
                                                        title={type}
                                                    >
                                                        {type}
                                                    </span>
                                                    <InputNumber
                                                        placeholder="Min"
                                                        value={rangeValue[0]}
                                                        onChange={minVal => {
                                                            // Store in condition metadata only - deep copy to avoid read-only property errors
                                                            const currentRanges: Record<
                                                                string,
                                                                {
                                                                    min?: number;
                                                                    max?: number;
                                                                    outstanding?: boolean;
                                                                    normalOnly?: boolean;
                                                                }
                                                            > = {};
                                                            Object.keys(measurementValueRanges).forEach(key => {
                                                                const existingRange = measurementValueRanges[key];
                                                                if (existingRange) {
                                                                    currentRanges[key] = {
                                                                        min: existingRange.min,
                                                                        max: existingRange.max,
                                                                        outstanding: existingRange.outstanding,
                                                                        normalOnly: existingRange.normalOnly,
                                                                    };
                                                                }
                                                            });
                                                            if (!currentRanges[type]) {
                                                                currentRanges[type] = {};
                                                            }
                                                            currentRanges[type] = {
                                                                ...currentRanges[type],
                                                                min: minVal || undefined,
                                                                max: rangeValue[1],
                                                                outstanding: isOutstanding,
                                                                normalOnly: isNormalOnly,
                                                            };

                                                            // Update condition with metadata (will trigger API via useEmitFilters)
                                                            updateCondition(groupId, condition.id, {
                                                                measurementValueRanges: currentRanges,
                                                            } as any);
                                                        }}
                                                        style={{ flex: 1 }}
                                                        className={Styles.numberInput}
                                                    />

                                                    <InputNumber
                                                        placeholder="Max"
                                                        value={rangeValue[1]}
                                                        className={Styles.numberInput}
                                                        onChange={maxVal => {
                                                            // Store in condition metadata only - deep copy to avoid read-only property errors
                                                            const currentRanges: Record<
                                                                string,
                                                                {
                                                                    min?: number;
                                                                    max?: number;
                                                                    outstanding?: boolean;
                                                                    normalOnly?: boolean;
                                                                }
                                                            > = {};
                                                            Object.keys(measurementValueRanges).forEach(key => {
                                                                const existingRange = measurementValueRanges[key];
                                                                if (existingRange) {
                                                                    currentRanges[key] = {
                                                                        min: existingRange.min,
                                                                        max: existingRange.max,
                                                                        outstanding: existingRange.outstanding,
                                                                        normalOnly: existingRange.normalOnly,
                                                                    };
                                                                }
                                                            });
                                                            // Update or create range for this type
                                                            currentRanges[type] = {
                                                                min: rangeValue[0],
                                                                max: maxVal || undefined,
                                                                outstanding: isOutstanding,
                                                                normalOnly: isNormalOnly,
                                                            };

                                                            // Update condition with metadata (will trigger API via useEmitFilters)
                                                            updateCondition(groupId, condition.id, {
                                                                measurementValueRanges: currentRanges,
                                                            } as any);
                                                        }}
                                                        style={{ flex: 1 }}
                                                    />
                                                </div>
                                                <div className="flex gap-[4px]">
                                                    <Checkbox
                                                        checked={isOutstanding}
                                                        onChange={e => {
                                                            // Store in condition metadata only - deep copy to avoid read-only property errors
                                                            const currentRanges: Record<
                                                                string,
                                                                {
                                                                    min?: number;
                                                                    max?: number;
                                                                    outstanding?: boolean;
                                                                    normalOnly?: boolean;
                                                                }
                                                            > = {};
                                                            Object.keys(measurementValueRanges).forEach(key => {
                                                                const existingRange = measurementValueRanges[key];
                                                                if (existingRange) {
                                                                    currentRanges[key] = {
                                                                        min: existingRange.min,
                                                                        max: existingRange.max,
                                                                        outstanding: existingRange.outstanding,
                                                                        normalOnly: existingRange.normalOnly,
                                                                    };
                                                                }
                                                            });
                                                            // Update or create range for this type
                                                            // When outstanding is checked, uncheck normalOnly (mutually exclusive)
                                                            currentRanges[type] = {
                                                                min: rangeValue[0],
                                                                max: rangeValue[1],
                                                                outstanding: e.target.checked,
                                                                normalOnly: e.target.checked ? false : isNormalOnly,
                                                            };

                                                            // Update condition with metadata (will trigger API via useEmitFilters)
                                                            updateCondition(groupId, condition.id, {
                                                                measurementValueRanges: currentRanges,
                                                            } as any);
                                                        }}
                                                        className="[&_.ant-checkbox-label]:text-[12px] [&_.ant-checkbox-label]:text-[#3D4B56]"
                                                    >
                                                        Outstanding
                                                    </Checkbox>
                                                    <Checkbox
                                                        checked={isNormalOnly}
                                                        onChange={e => {
                                                            // Store in condition metadata only - deep copy to avoid read-only property errors
                                                            const currentRanges: Record<
                                                                string,
                                                                {
                                                                    min?: number;
                                                                    max?: number;
                                                                    outstanding?: boolean;
                                                                    normalOnly?: boolean;
                                                                }
                                                            > = {};
                                                            Object.keys(measurementValueRanges).forEach(key => {
                                                                const existingRange = measurementValueRanges[key];
                                                                if (existingRange) {
                                                                    currentRanges[key] = {
                                                                        min: existingRange.min,
                                                                        max: existingRange.max,
                                                                        outstanding: existingRange.outstanding,
                                                                        normalOnly: existingRange.normalOnly,
                                                                    };
                                                                }
                                                            });
                                                            // Update or create range for this type
                                                            // When normalOnly is checked, uncheck outstanding (mutually exclusive)
                                                            currentRanges[type] = {
                                                                min: rangeValue[0],
                                                                max: rangeValue[1],
                                                                outstanding: e.target.checked ? false : isOutstanding,
                                                                normalOnly: e.target.checked,
                                                            };

                                                            // Update condition with metadata (will trigger API via useEmitFilters)
                                                            updateCondition(groupId, condition.id, {
                                                                measurementValueRanges: currentRanges,
                                                            } as any);
                                                        }}
                                                        className="[&_.ant-checkbox-label]:text-[12px] [&_.ant-checkbox-label]:text-[#3D4B56]"
                                                    >
                                                        Normal
                                                    </Checkbox>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {/* Show date range and count filters for note_type and procedure_type (per-type filters) */}
                            {hasNoteOrProcedureValues && (
                                <div className="mt-2 flex flex-col gap-3 rounded-md border border-[#e5e7eb] bg-white p-3">
                                    <div className="text-[12px] font-medium text-[#666]">
                                        {isNoteType ? 'Note Filters:' : 'Procedure Filters:'}
                                    </div>

                                    {(isNoteType && noteTypeValues.length > 0) ||
                                    (isProcedureType && procedureTypeValues.length > 0) ? (
                                        // Show filters for each selected type (both note_type and procedure_type)
                                        <div className="flex flex-col gap-3">
                                            {(isNoteType ? noteTypeValues : procedureTypeValues).map(
                                                (type: string, idx: number) => {
                                                    // Get filters for this specific type
                                                    const currentFilters = isNoteType
                                                        ? noteTypeFilters
                                                        : procedureTypeFilters;
                                                    const typeFilters = currentFilters[type] || {};
                                                    const typeDateRange = typeFilters.dateRange;
                                                    const typeCountMin = typeFilters.countMin;
                                                    const typeCountMax = typeFilters.countMax;
                                                    const metadataKey = isNoteType
                                                        ? 'noteTypeFilters'
                                                        : 'procedureTypeFilters';

                                                    return (
                                                        <div
                                                            key={`${type}-${idx}`}
                                                            className="flex flex-col gap-2 rounded border border-gray-200 bg-gray-50 p-2"
                                                        >
                                                            <div
                                                                className="max-w-[150px] truncate text-[11px] font-medium text-[#666]"
                                                                title={type}
                                                            >
                                                                {type}:
                                                            </div>

                                                            {/* Date Range for this type */}
                                                            <div className="flex flex-col gap-1">
                                                                <span className="text-[10px] text-[#888]">
                                                                    Date Range:
                                                                </span>
                                                                <DatePicker.RangePicker
                                                                    size="small"
                                                                    value={
                                                                        typeDateRange?.start && typeDateRange?.end
                                                                            ? [
                                                                                  dayjs(typeDateRange.start),
                                                                                  dayjs(typeDateRange.end),
                                                                              ]
                                                                            : null
                                                                    }
                                                                    onChange={(
                                                                        dates: [Dayjs | null, Dayjs | null] | null
                                                                    ) => {
                                                                        // Deep copy current filters
                                                                        const updatedFilters: Record<
                                                                            string,
                                                                            {
                                                                                dateRange?: {
                                                                                    start: string;
                                                                                    end: string;
                                                                                };
                                                                                countMin?: number;
                                                                                countMax?: number;
                                                                            }
                                                                        > = {};
                                                                        Object.keys(currentFilters).forEach(key => {
                                                                            updatedFilters[key] = {
                                                                                ...currentFilters[key],
                                                                            };
                                                                        });

                                                                        // Initialize if doesn't exist
                                                                        if (!updatedFilters[type]) {
                                                                            updatedFilters[type] = {};
                                                                        }

                                                                        if (dates && dates[0] && dates[1]) {
                                                                            updatedFilters[type].dateRange = {
                                                                                start: dates[0].format('YYYY-MM-DD'),
                                                                                end: dates[1].format('YYYY-MM-DD'),
                                                                            };
                                                                        } else {
                                                                            updatedFilters[type].dateRange = undefined;
                                                                        }

                                                                        updateCondition(groupId, condition.id, {
                                                                            [metadataKey]: updatedFilters,
                                                                        } as any);
                                                                    }}
                                                                    format="YYYY-MM-DD"
                                                                    className={Styles.dateRangePicker}
                                                                    style={{ width: '100%' }}
                                                                />
                                                            </div>

                                                            {/* Count Range for this type */}
                                                            <div className="flex flex-col gap-1">
                                                                <span className="text-[10px] text-[#888]">
                                                                    Count Range:
                                                                </span>
                                                                <div className="flex items-center gap-2">
                                                                    <InputNumber
                                                                        placeholder="Min"
                                                                        size="small"
                                                                        min={0}
                                                                        value={typeCountMin}
                                                                        onChange={val => {
                                                                            // Deep copy current filters
                                                                            const updatedFilters: Record<
                                                                                string,
                                                                                {
                                                                                    dateRange?: {
                                                                                        start: string;
                                                                                        end: string;
                                                                                    };
                                                                                    countMin?: number;
                                                                                    countMax?: number;
                                                                                }
                                                                            > = {};
                                                                            Object.keys(currentFilters).forEach(key => {
                                                                                updatedFilters[key] = {
                                                                                    ...currentFilters[key],
                                                                                };
                                                                            });

                                                                            // Initialize if doesn't exist
                                                                            if (!updatedFilters[type]) {
                                                                                updatedFilters[type] = {};
                                                                            }

                                                                            updatedFilters[type].countMin =
                                                                                val || undefined;

                                                                            updateCondition(groupId, condition.id, {
                                                                                [metadataKey]: updatedFilters,
                                                                            } as any);
                                                                        }}
                                                                        className={Styles.numberInput}
                                                                        style={{ flex: 1 }}
                                                                    />
                                                                    <span className="text-xs text-gray-400">-</span>
                                                                    <InputNumber
                                                                        placeholder="Max"
                                                                        size="small"
                                                                        min={0}
                                                                        value={typeCountMax}
                                                                        onChange={val => {
                                                                            // Deep copy current filters
                                                                            const updatedFilters: Record<
                                                                                string,
                                                                                {
                                                                                    dateRange?: {
                                                                                        start: string;
                                                                                        end: string;
                                                                                    };
                                                                                    countMin?: number;
                                                                                    countMax?: number;
                                                                                }
                                                                            > = {};
                                                                            Object.keys(currentFilters).forEach(key => {
                                                                                updatedFilters[key] = {
                                                                                    ...currentFilters[key],
                                                                                };
                                                                            });

                                                                            // Initialize if doesn't exist
                                                                            if (!updatedFilters[type]) {
                                                                                updatedFilters[type] = {};
                                                                            }

                                                                            updatedFilters[type].countMax =
                                                                                val || undefined;

                                                                            updateCondition(groupId, condition.id, {
                                                                                [metadataKey]: updatedFilters,
                                                                            } as any);
                                                                        }}
                                                                        className={Styles.numberInput}
                                                                        style={{ flex: 1 }}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                }
                                            )}
                                        </div>
                                    ) : null}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </Space.Compact>
        </div>
    );
}
